// React Dependencies
import React, { useState, useEffect, useRef } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import MaterialTable from 'material-table';
import { FormControl, Button, Box, InputLabel, Select, MenuItem, TextField } from '@material-ui/core';

// Internal Dependencies
import ApiService from '../../services.js/apiService';
import { getReceiptUploadsByParams } from '../../actions/admin/receiptUploadAction';
import { IsEmpty, IsTrue } from '../../common/checks';
import { formatDateToDayMonthNameYear, showDateInSGTime } from '../../common/util';
import Constants from '../../common/constants';
import DateRangePicker from '../global/form/DateRangePicker';

const ReceiptUploadsTable = (props) => {
  const table = useRef(null);
  const columns = [
    { title: 'Id', field: 'id', filtering: false, width: '5%' },
    { title: 'Status', field: 'status', filtering: false, width: '5%' },
    { title: 'Loyalty Card Name', field: 'loyaltyCardName', filtering: false },
    {
      title:       'Upload Date',
      field:       'createdAt',
      sorting:     true,
      defaultSort: 'asc',
      render:      (receiptUpload) => (receiptUpload.createdAt !== null ? `${formatDateToDayMonthNameYear(showDateInSGTime(receiptUpload.createdAt), true)}` : '-'),
    },
    {
      title:  'Receipt Date',
      field:  'receiptDate',
      render: (receiptUpload) => (receiptUpload.receiptDate !== null ? `${formatDateToDayMonthNameYear(showDateInSGTime(receiptUpload.receiptDate))}` : '-'),
    },
    { title: 'Receipt No', field: 'receiptNo', filtering: false },
    { title: 'Amount', field: 'amount', filtering: false, type: 'currency' },
    {
      title:  'Outlet',
      field:  'outlet',
      render: (receiptUpload) => (!IsEmpty(receiptUpload.outlet) ? receiptUpload.outlet : '-'),
    },
    {
      title:     'Process Remarks',
      field:     'processRemarks',
      filtering: false,
      render:    (receiptUpload) => {
        const maxLength = 50;
        if (!IsEmpty(receiptUpload.processRemarks)) {
          return (receiptUpload.processRemarks.length >= maxLength) ? `${receiptUpload.processRemarks.substring(0, maxLength)}...` : receiptUpload.processRemarks;
        }
      },
    },
  ];

  const initializeFocusValue = {
    receiptUploadIdFocus: false,
  };

  const [pageSize, setPageSize] = useState(20);
  const [autoFocus, setAutoFocus] = useState(initializeFocusValue);
  const { receiptUploadIdFocus } = autoFocus;

  const [loyaltyCardOptions, setLoyaltyCardOptions] = useState([]);

  const [triggerClearFilters, setTriggerClearFilters] = useState(false);
  const [receiptUploadId, setReceiptUploadId] = useState('');
  const [status, setStatus] = useState('Pending');
  const [createdAt, setCreatedAt] = useState([]);
  const [loyaltyCardId, setLoyaltyCardId] = useState('');
  const [selectedLoyaltyCard, setSelectedLoyaltyCard] = useState('');

  async function fetchLoyaltyCardsData() {
    try {
      const loyaltyCards = await ApiService.getLoyaltyCards(null, { status: Constants.Status.Live });
      const options = loyaltyCards.rows.map((loyaltyCard) => ({
        value: loyaltyCard.id,
        label: loyaltyCard.name,
      }));

      setLoyaltyCardOptions(options);
    } catch (error) {
      toast.error(error.message);
    }
  }

  function searchReceiptUploadId(event) {
    setReceiptUploadId(event.target.value);
    setAutoFocus({ receiptUploadIdFocus: true });
  }

  const setDatesFilter = (dates) => {
    if (!IsEmpty(dates) && dates.length > 1) {
      const arrDate1 = new Date(dates[0]);
      const arrDate2 = new Date(dates[1]);
      const dateFrom = `${arrDate1.getFullYear()}-${(arrDate1.getMonth() + 1).toString().padStart(2, '0')}-${arrDate1.getDate().toString().padStart(2, '0')}`;
      const dateTo = `${arrDate2.getFullYear()}-${(arrDate2.getMonth() + 1).toString().padStart(2, '0')}-${arrDate2.getDate().toString().padStart(2, '0')}`;

      setCreatedAt([dateFrom, dateTo]);
    } else {
      setCreatedAt([]);
    }
  };

  const handleChange = (event) => {
    if (event.target.name === 'sortByLoyaltyCard') {
      setSelectedLoyaltyCard(event.target.value);
      setLoyaltyCardId(event.target.value);
    }

    if (event.target.name === 'status') {
      setStatus(event.target.value);
    }
  };

  const clearFilters = () => {
    setReceiptUploadId('');
    setStatus('');
    setCreatedAt('');
    setLoyaltyCardId('');
    setSelectedLoyaltyCard('');

    setTriggerClearFilters(true);
  };

  useEffect(() => {
    fetchLoyaltyCardsData();
  }, [selectedLoyaltyCard]);

  useEffect(() => {
    if (IsTrue(triggerClearFilters)) {
      table.current.onQueryChange();
      setTriggerClearFilters(false);
    }
  }, [triggerClearFilters]);

  return (
    <MaterialTable
      tableRef={table}
      className="material-table"
      title=""
      columns={columns}
      onChangeRowsPerPage={(pageSizeTable) => {
        setAutoFocus(initializeFocusValue);
        setPageSize(pageSizeTable);
      }}
      data={(query) => new Promise(async (resolve, reject) => {
        try {
          query.id = receiptUploadId;
          query.status = status;
          query.createdAt = createdAt;
          query.loyaltyCardId = loyaltyCardId;
          query.pageNo = query.page;

          // SET DEFAULT SORT BY ID
          query.orderBy = 'id';
          query.orderDirection = 'ASC';
          query.order = { col: query.orderBy, dir: query.orderDirection };

          const result = await props.getReceiptUploadsByParams(query);
          let { rows, totalCount } = result;
          let { page } = query;

          if (rows.length === 0) {
            page = 0;

            if (totalCount !== 0) {
              const resultFirstPage = await props.getReceiptUploadsByParams(
                { ...query, page },
              );

              rows = resultFirstPage.rows;
            }
          }

          resolve({
            data: rows,
            page,
            totalCount,
          });
        } catch (err) {
          toast.error(err);
          resolve({
            data:       [],
            page:       0,
            totalCount: 0,
          });
        }
      })}
      actions={[
        {
          icon:    'edit',
          tooltip: 'Process Receipt Upload',
          onClick: (event, receiptUpload) => {
            window.open(`/admin/receiptUpload/${receiptUpload.id}`, '_blank');
          },
        },
      ]}
      options={{
        pageSize,
        actionsColumnIndex:  -1,
        emptyRowsWhenPaging: false,
        search:              false,
        sorting:             false,
      }}
      components={{
        Toolbar: () => (
          <div className="pt-3">
            <Box display="flex" justifyContent="flex-start" m={0} p={1} bgcolor="background.paper">
              <Box p={1} width="15%">
                <FormControl fullWidth>
                  <Button variant="outlined" size="medium" color="secondary" onClick={() => { clearFilters(); }}>Clear Filters</Button>
                </FormControl>
              </Box>
            </Box>

            <Box display="flex" justifyContent="flex-start" m={0} p={1} bgcolor="background.paper">
              <Box p={1} width="5%">
                <FormControl fullWidth>
                  <TextField
                    label="Id"
                    type="number"
                    value={receiptUploadId || ''}
                    onChange={searchReceiptUploadId}
                    autoFocus={receiptUploadIdFocus}
                  />
                </FormControl>
              </Box>

              <Box p={1} width="15%">
                <FormControl fullWidth>
                  <InputLabel id="status-filter-label">Status</InputLabel>
                  <Select
                    labelId="status-filter-label"
                    value={status}
                    name="status"
                    onChange={handleChange}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="Accepted">Accepted</MenuItem>
                    <MenuItem value="Rejected">Rejected</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box p={1} width="20%">
                <FormControl fullWidth>
                  <p style={{ color: '#888888' }}>Uploaded at</p>
                  <DateRangePicker callback={(d) => setDatesFilter(d)} dateVal={createdAt} />
                </FormControl>
              </Box>

              <Box p={1} width="25%">
                <FormControl fullWidth>
                  <InputLabel id="loyalty-card-label">Loyalty Card</InputLabel>
                  <Select
                    id="loyalty-card-label"
                    value={selectedLoyaltyCard}
                    name="sortByLoyaltyCard"
                    onChange={handleChange}
                  >
                    <MenuItem value="">All</MenuItem>
                    {
                      loyaltyCardOptions.map((loyaltyCard) => (
                        <MenuItem key={loyaltyCard.value} value={loyaltyCard.value}>{loyaltyCard.label}</MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
              </Box>

              <Box p={1} width="15%">
                <FormControl fullWidth>
                  <Button variant="contained" className="mt-3" size="medium" color="secondary" onClick={() => { table.current.onQueryChange(); }}>Search</Button>
                </FormControl>
              </Box>
            </Box>
          </div>
        ),
      }}
    />
  );
};

ReceiptUploadsTable.propTypes = {
  getReceiptUploadsByParams: PropTypes.func.isRequired,
};

const mapActionToProps = {
  getReceiptUploadsByParams,
};

export default connect(
  null,
  mapActionToProps,
)(ReceiptUploadsTable);
